import { initContract } from '@ts-rest/core';
import { z } from 'zod';

const c = initContract();

const errorResponseSchema = z.object({
  error: z.object({ message: z.string(), code: z.string().optional() }),
});

export const FORM_TRANSFER_FORMAT = 'skewb-works.form';
export const FORM_TRANSFER_VERSION = 1;

// Controls are the form builder's own JSON (q_id, mapping_key, hidden, read_only, conditions…).
// Carried verbatim so anything the builder adds later survives a round trip.
export const formTransferControlSchema = z.record(z.string(), z.unknown());

const INT32_MAX = 2147483647;
const MAX_SECTIONS_PER_FORM = 200;
const MAX_CONTROLS_PER_SECTION = 1000;

// forms_sections.sequence / section_number / max_repeat are Postgres 32-bit integers.
const nonNegativeInt32 = z.number().int().min(0).max(INT32_MAX);

export const formTransferSectionSchema = z.object({
  sequence: nonNegativeInt32,
  section_number: nonNegativeInt32.nullish(),
  section_name: z.string().trim().min(1).max(5000),
  section_description: z.string().max(10000).nullish(),
  is_table_view: z.boolean().nullish(),
  is_repeatable: z.boolean().nullish(),
  max_repeat: nonNegativeInt32.nullish(),
  controls: z.array(formTransferControlSchema).max(MAX_CONTROLS_PER_SECTION),
});

export const formTransferFormTypeSchema = z.object({
  name: z.string().trim().min(1).max(255),
  alias: z.string().max(50).nullish(),
});

export const formTransferFormSchema = z.object({
  name: z.string().trim().min(1).max(500),
  description: z.string().max(10000).nullish(),
  image_url: z.string().max(10000).nullish(),
  layout: z.string().max(50).nullish(),
  is_dynamic: z.boolean().nullish(),
  compliant: z.union([z.boolean(), z.string().max(50)]).nullish(),
  can_take_action: z.boolean().nullish(),
  form_type: formTransferFormTypeSchema,
  sections: z.array(formTransferSectionSchema).max(MAX_SECTIONS_PER_FORM),
});

export const formTransferPackageSchema = z.object({
  format: z.literal(FORM_TRANSFER_FORMAT),
  version: z.literal(FORM_TRANSFER_VERSION),
  exported_at: z.string(),
  form: formTransferFormSchema,
});

export const formTransferImportResponseSchema = z.object({
  form_id: z.number().int(),
  name: z.string(),
  renamed: z.boolean(),
  form_type_created: z.boolean(),
});

export type FormTransferPackage = z.infer<typeof formTransferPackageSchema>;
export type FormTransferImportResponse = z.infer<typeof formTransferImportResponseSchema>;

export const formTransferContract = c.router({
  exportForm: {
    method: 'GET',
    path: '/:id/export',
    pathParams: z.object({ id: z.coerce.number().int().positive() }),
    responses: {
      200: formTransferPackageSchema,
      404: errorResponseSchema,
      422: errorResponseSchema,
      500: errorResponseSchema,
    },
    summary: 'Export a form definition (form, sections, controls, form type) as a portable package',
  },
  importForm: {
    method: 'POST',
    path: '/import',
    body: formTransferPackageSchema,
    responses: {
      201: formTransferImportResponseSchema,
      400: errorResponseSchema,
      500: errorResponseSchema,
    },
    summary: 'Create a new form from an exported package',
  },
});
